import { useMemo } from 'react';

import * as monthUtils from '@actual-app/core/shared/months';
import {
  addDays,
  netPay,
  planBalance,
  planFortnights,
  reserveRoom,
} from '@actual-app/core/shared/mtxbudget';
import type {
  Fortnight,
  Occurrence,
  Reserve,
} from '@actual-app/core/shared/mtxbudget';
import {
  laterUntil,
  toComplete,
  withArrangements,
  withProvided,
} from '@actual-app/core/shared/mtxbudget-completer';
import type { ToComplete } from '@actual-app/core/shared/mtxbudget-completer';
import {
  cardRoomFromBank,
  cashCharges,
} from '@actual-app/core/shared/mtxbudget-parcours';
import {
  expandSchedules,
  investmentCharges,
  MTX_CONFIG_PREF,
  parseMtxConfig,
  planStart,
  withScheduledRepayments,
} from '@actual-app/core/shared/mtxbudget-schedules';
import type {
  ConfiguredDebt,
  MtxConfig,
} from '@actual-app/core/shared/mtxbudget-schedules';
import { q } from '@actual-app/core/shared/query';

import { useAccountBalances } from '#hooks/useAccountBalances';
import { useAccounts } from '#hooks/useAccounts';
import { usePayees } from '#hooks/usePayees';
import { useSchedules } from '#hooks/useSchedules';
import { useSyncedPref } from '#hooks/useSyncedPref';

import { bankAvailable, bankDataDate, useBankReads } from './bankReads';
import { useByContent } from './byContent';
import { useUnreadAccounts } from './recalcul';
import { useAccountsWithBankMovements } from './useTransactions';

// Thirteen pays, half a year: Ma quinzaine and the walk week by week read
// the hours from this one plan, so both screens give the same figure.
const FORTNIGHTS = 13;
const HORIZON_DAYS = 14 * (FORTNIGHTS + 1);
// A repayment can be planned well beyond the cash plan.
const REPAYMENT_HORIZON_DAYS = 3650;

export type Mtxbudget = {
  isLoading: boolean;
  config: MtxConfig | null;
  saveConfig: (config: MtxConfig) => void;
  today: string;
  /** What the salary has to pay: charges put on a card are left out. */
  occurrences: Occurrence[];
  /** Every charge, those put on a card included. */
  charges: Occurrence[];
  plan: Fortnight[];
  /** Charges to pay with the next pay, late ones included, paid or not. */
  toPay: Occurrence[];
  /** The first pay whose hours can still be changed. */
  open: Fortnight | null;
  available: number;
  /** Debts with the live balance of their account, when they have one. */
  debts: ConfiguredDebt[];
  /** Overdrafts and lines, with only what their account has left of them. */
  reserves: Reserve[];
  /** What a month at the guaranteed hours leaves once every charge is paid. */
  monthlyMargin: number;
  /** The amounts the banks do not send, to ask of the user now. */
  toComplete: ToComplete[];
  /** Gives an amount, in cents as typed: positive for a charge. */
  provide: (item: ToComplete, cents: number) => void;
  /** Goes back to the estimate for an amount given earlier. */
  forget: (id: string) => void;
  /** « Plus tard »: asks again in three days, or on the eve of the charge. */
  putOff: (item: ToComplete) => void;
};

export function useMtxbudget(): Mtxbudget {
  const [raw, setRaw] = useSyncedPref(MTX_CONFIG_PREF);
  const config = useMemo(() => parseMtxConfig(raw), [raw]);
  const query = useMemo(() => q('schedules').select('*'), []);
  const { schedules, statuses, isLoading } = useSchedules({ query });
  const today = monthUtils.currentDay();

  const cashIds = config?.cashAccountIds ?? [];
  const debtAccountIds = (config?.debts ?? []).flatMap(d =>
    d.accountId ? [d.accountId] : [],
  );
  const reserveAccountIds = (config?.reserves ?? []).flatMap(r =>
    r.accountId ? [r.accountId] : [],
  );
  const book = useAccountBalances([
    ...cashIds,
    ...debtAccountIds,
    ...reserveAccountIds,
  ]);
  const { data: accounts = [] } = useAccounts();
  // The bank's own balance for a loan or a savings account whose movements
  // the bank never sends; the book for the others.
  const withMovements = useAccountsWithBankMovements([
    ...cashIds,
    ...debtAccountIds,
    ...reserveAccountIds,
  ]);
  // Read again after every bank sync: a balance given by the user stands
  // until the bank sends data of a later day.
  useBankReads();
  const balanceOf = (id: string) => {
    const account = accounts.find(a => a.id === id);
    const given = config?.providedBalances[id];
    const dataDate = account ? bankDataDate(account) : null;
    if (given && (dataDate === null || given.on >= dataDate)) {
      return given.amount;
    }
    return planBalance(account, book[id] ?? null, withMovements.has(id));
  };
  const available = cashIds.reduce((sum, id) => sum + balanceOf(id), 0);

  const paid = useMemo(
    () =>
      new Set(
        [...statuses].flatMap(([id, status]) =>
          status === 'paid' ? [id] : [],
        ),
      ),
    [statuses],
  );
  // A pay the banks have not shown yet still counts, with what fell due since.
  const from = config ? planStart(config.pay, schedules, today, paid) : today;

  // Every charge, an amount the user gave in place of its estimate.
  const charges = useMemo(() => {
    // A payment switched off by hand is not planned for, here or anywhere.
    const skipped = config?.skipped ?? {};
    const planned = expandSchedules(
      schedules,
      from,
      addDays(today, HORIZON_DAYS),
      paid,
    )
      // An investment to pay before the debts is a charge like any other.
      .concat(
        investmentCharges(
          config?.projects ?? [],
          from,
          addDays(today, HORIZON_DAYS),
        ),
      )
      .filter(occurrence => !skipped[occurrence.id])
      .sort((a, b) => a.date.localeCompare(b.date));
    return withArrangements(
      withProvided(planned, config?.provided ?? {}),
      config?.arrangements ?? {},
    ).sort((a, b) => a.date.localeCompare(b.date));
  }, [schedules, paid, from, today, config]);
  const repayments = useMemo(() => {
    const tied = new Set(
      (config?.debts ?? []).flatMap(d => d.repaymentScheduleIds ?? []),
    );
    return expandSchedules(
      schedules.filter(schedule => tied.has(schedule.id)),
      today,
      addDays(today, REPAYMENT_HORIZON_DAYS),
      new Set(),
    );
  }, [config, schedules, today]);

  const debts = withScheduledRepayments(
    (config?.debts ?? []).map(debt => {
      const balance = debt.accountId ? balanceOf(debt.accountId) : undefined;
      const account = accounts.find(a => a.id === debt.accountId);
      const available = account ? bankAvailable(account) : null;
      return {
        ...debt,
        ...(balance == null ? {} : { balance: Math.max(0, -balance) }),
        // What the bank still lets go on a card, pending purchases included.
        ...(debt.creditLimit !== undefined && available !== null
          ? { bankRoom: cardRoomFromBank(available) }
          : {}),
      };
    }),
    repayments,
  );

  // A charge put on a card leaves no cash: the salary pays the card's own
  // payments, not what was bought with it. Unless the card has no room left
  // for it: then it is turned down, and the salary pays it after all.
  const occurrences = useByContent(cashCharges(charges, debts));

  const plan = useMemo(
    () =>
      config
        ? planFortnights({
            config: config.pay,
            today,
            from,
            occurrences,
            pays: config.pays,
            available,
            count: FORTNIGHTS,
            incomes: config.extraIncomes,
          })
        : [],
    [config, today, from, occurrences, available],
  );

  const toPayEnd = plan[0] ? addDays(plan[0].nextPayday, -1) : today;
  const toPay = useMemo(
    () =>
      expandSchedules(schedules, addDays(today, -31), toPayEnd).filter(
        occurrence => occurrence.amount < 0,
      ),
    [schedules, today, toPayEnd],
  );

  const reserves = (config?.reserves ?? []).map(reserve => ({
    ...reserve,
    available: reserveRoom(
      reserve,
      reserve.accountId ? balanceOf(reserve.accountId) : undefined,
      reserve.accountId !== undefined && cashIds.includes(reserve.accountId),
    ),
  }));

  let monthlyMargin = 0;
  if (config) {
    const horizonEnd = addDays(today, 56);
    const charges = occurrences
      // A one-off is not a monthly charge: it must not weigh on every month.
      .filter(o => o.amount < 0 && o.date < horizonEnd && o.recurring !== false)
      .reduce((sum, o) => sum - o.amount, 0);
    const min = config.pay.minHoursPerWeek;
    monthlyMargin = Math.round(
      ((netPay(config.pay, [min, min]) - charges / 4) * 26) / 12,
    );
  }

  // A schedule that sends money to another account (a card, a line, a loan)
  // pays a statement: its amount is a minimum, not a bill.
  const { data: payees = [] } = usePayees();
  const transferPayees = new Set(
    payees.filter(payee => payee.transfer_acct).map(payee => payee.id),
  );
  const statementScheduleIds = new Set(
    schedules
      .filter(schedule => transferPayees.has(schedule._payee))
      .map(schedule => schedule.id),
  );
  const unread = useUnreadAccounts();
  const items = config
    ? toComplete({
        occurrences: charges,
        today,
        provided: config.provided,
        providedBalances: config.providedBalances,
        later: config.later,
        statementScheduleIds,
        unread: unread
          .filter(account => cashIds.includes(account.id))
          .map(account => ({
            accountId: account.id,
            name: account.name,
            balance: balanceOf(account.id),
          })),
      })
    : [];

  function save(next: MtxConfig) {
    setRaw(JSON.stringify(next));
  }

  return {
    isLoading,
    config,
    saveConfig: save,
    today,
    occurrences,
    charges,
    plan,
    toPay,
    open: plan.find(f => f.status === 'open' || f.status === 'future') ?? null,
    available,
    debts,
    reserves,
    monthlyMargin,
    toComplete: items,
    provide: (item, cents) => {
      if (!config) {
        return;
      }
      const on = today;
      if (item.kind === 'balance') {
        save({
          ...config,
          providedBalances: {
            ...config.providedBalances,
            [item.accountId]: { amount: cents, on },
          },
        });
        return;
      }
      // A charge given as a positive amount leaves the account.
      save({
        ...config,
        provided: {
          ...config.provided,
          [item.id]: { amount: -Math.abs(cents), on },
        },
      });
    },
    forget: id => {
      if (!config) {
        return;
      }
      const { [id]: _forgotten, ...provided } = config.provided;
      const { [id.replace(/^balance\//, '')]: _balance, ...providedBalances } =
        config.providedBalances;
      save({ ...config, provided, providedBalances });
    },
    putOff: item => {
      if (!config) {
        return;
      }
      save({
        ...config,
        later: {
          ...config.later,
          [item.id]: laterUntil(
            today,
            item.kind === 'amount' ? item.date : undefined,
          ),
        },
      });
    },
  };
}
